import React from "react";
import { Page, View, Text } from "@react-pdf/renderer";

import getPDFTranslations from "utils/translation/f280-pdf";
import { APP_VERSION } from "utils/constants/version";

import { styles } from "./pdfStyles";
import { Row, NumField, SmallTitle, Field, Checkbox } from "./pdfPrimitives";
import { TitleBar, Footer, DisclaimerProjectRow } from "./pdfShared";

// Right-hand compound cell: two fields side by side. Any internal vertical rule
// is drawn with `borderRight` on the left field (not a standalone divider View)
// so every column line uses the same method and stays straight down the page.
const SplitCell = ({ children, style }) => (
    <View style={[styles.fullFlex, { flexDirection: "row" }, style]}>{children}</View>
);

// Compact "Style A/B/C:" table cell used by the enclosure component tables.
const StyleField = ({ letter, value, num, borderRight, t }) => (
    <Field
        label={t("f280pdf.style", `Style ${letter}: [!!]`, { letter })}
        value={value || ""}
        num={num}
        autoLabel
        labelStyle={{ ...styles.smallLabel, width: 34 }}
        containerStyle={borderRight ? styles.borderRight : undefined}
    />
);

// Page 2 of the F280 report — the "CALCULATION BASED ON" input summary sheet
// (CSA Standard Form No. 1). Vector react-pdf rebuild; data/translation keys
// are unchanged from the legacy raster version.
const FormPage = ({ name, displayData, inputFieldsData, language, includeRoomByRoomResults }) => {
    const { issuedForTheUseOf, projectNo, lot, site, dimInfoBasedOn } = inputFieldsData;

    const { buildingLocation = {}, calculationBasedOn = {}, components = {}, designConditions } = displayData || {};
    const { cityProvince = "", postalCode = "", address = "" } = buildingLocation || {};

    const {
        airtightness = "",
        attachment = "",
        frontFacing = "",
        hrv = "",
        internalShading = "",
        internalShadingAssumed = "",
        numStoreys = "",
        occupants = "",
        windSheltering = "",
        recoveryEff = "",
        units = "",
        ventilated = "",
        weatherLocation = "",
        windExposure = "",
        hrvAtre,
        soilConductivity,
        airtightnessAssumed = "",
        frontFacingAssumed = "",
        attachment_fr = "",
        numStoreys_fr = "",
        frontFacing_fr = "",
        windExposure_fr = "",
        windSheltering_fr = "",
        soilConductivity_fr = "",
        internalShading_fr = "",
        internalShadingAssumed_fr = "",
        occupantsAssumed = "",
        occupantsAssumed_fr = "",
        ventilated_fr = "",
        hrv_fr = "",
        recoveryEff_fr = "",
        airtightnessAssumed_fr = "",
        frontFacingAssumed_fr = "",
    } = calculationBasedOn || {};

    const langLabel = {
        en: {
            attachment,
            numStoreys,
            frontFacing,
            windExposure,
            windSheltering,
            soilConductivity,
            internalShading,
            internalShadingAssumed,
            occupantsAssumed,
            ventilated,
            hrv,
            recoveryEff,
            airtightnessAssumed,
            frontFacingAssumed,
        },
        fr: {
            attachment: attachment_fr,
            numStoreys: numStoreys_fr,
            frontFacing: frontFacing_fr,
            windExposure: windExposure_fr,
            windSheltering: windSheltering_fr,
            soilConductivity: soilConductivity_fr,
            internalShading: internalShading_fr,
            internalShadingAssumed: internalShadingAssumed_fr,
            occupantsAssumed: occupantsAssumed_fr,
            ventilated: ventilated_fr,
            hrv: hrv_fr,
            recoveryEff: recoveryEff_fr,
            airtightnessAssumed: airtightnessAssumed_fr,
            frontFacingAssumed: frontFacingAssumed_fr,
        },
    }[language];

    const { cooling = {}, heating = {} } = designConditions || {};
    const {
        aboveGradeWalls = [],
        belowGradeWalls = [],
        ceilings = [],
        doors = [],
        exposedFloors = [],
        floorsOnSoil = [],
        skylights = [],
        windows = [],
    } = components || {};

    const { t } = getPDFTranslations(language);
    const modelName = name || "";
    const totalPages = includeRoomByRoomResults ? 3 : 2;

    return (
        <Page size="LETTER" style={styles.page}>
            {/* wrap={false}: keep the whole form on this single page. Several cells
                (soil conductivity, window/skylight descriptions) wrap to 2-3 lines
                depending on the data, and when the total is a hair over the page
                react-pdf otherwise pushes just the footer onto a near-empty extra
                page. Treating the body as one unbreakable block prevents that. */}
            <View style={styles.body} wrap={false}>
                <TitleBar
                    title={t("title", "CSA STANDARD F280-12 COMPLIANCE [!!]")}
                    subtitle={t(
                        "f280pdf.subtitle",
                        "NBC 2015: 9.33.5.1.; 9.36.3.2. & 9.36.5.15; NBC 2020; 9.33.5.1.; 9.36.3.2.; 9.36.5.15 (5); 9.36.8.9. (1) [!!]"
                    )}
                    rightPrimary={t("f280pdf.standardForm.1", "CSA-F280-M12 [!!]")}
                    rightSecondary={t("f280pdf.standardForm.2", "Standard Form No. 1 [!!]")}
                />

                {/* Disclaimer + project number */}
                <DisclaimerProjectRow t={t} issuedForTheUseOf={issuedForTheUseOf} projectNo={projectNo} />

                {/* Building location */}
                <SmallTitle>{t("f280pdf.buildingLoc", "BUILDING LOCATION [!!]")}</SmallTitle>
                <Row>
                    <Field
                        label={t("f280pdf.model", "Model: [!!]")}
                        value={modelName}
                        num="3"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.site", "Site: [!!]")}
                            value={site}
                            num="6"
                            containerStyle={styles.borderRight}
                        />
                        <Field label={t("f280pdf.lot", "Lot: [!!]")} value={lot} num="7" />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.address", "Address: [!!]")}
                        value={address}
                        num="4"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={`${t("f280pdf.city", "City [!!]")}/ ${t("f280pdf.province", "Prov: [!!]")}`}
                            value={cityProvince}
                            num="5"
                            labelStyle={{ width: 40 }}
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={`${t("f280pdf.postalCode.1", "Post. [!!]")} ${t("f280pdf.postalCode.2", "code: [!!]")}`}
                            value={postalCode}
                            num="8"
                        />
                    </SplitCell>
                </Row>

                {/* Calculation based on */}
                <SmallTitle supText={t("f280pdf.seeFollowingPage", "(See Following Page For Results) [!!]")}>
                    {t("f280pdf.calculationBasedOn", "CALCULATION BASED ON [!!]")}
                </SmallTitle>
                <Row>
                    <Field
                        label={t("f280pdf.infoBasedOn", "Dimensional information based on: [!!]")}
                        value={dimInfoBasedOn}
                        num="9"
                        autoLabel
                    />
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.attachment", "Attachment: [!!]")}
                        value={langLabel.attachment}
                        num="10"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.frontFacing", "Front facing: [!!]")}
                            value={langLabel.frontFacing}
                            num="16"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.assumed?", "Assumed? [!!]")}
                            value={langLabel.frontFacingAssumed}
                            num="17"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.numStories", "#. of stories: [!!]")}
                        value={langLabel.numStoreys}
                        num="11"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.airTightness", "Air tightness: [!!]")}
                            value={airtightness}
                            num="18"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.assumed?", "Assumed? [!!]")}
                            value={langLabel.airtightnessAssumed}
                            num="19"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.weatherLocation", "Weather location: [!!]")}
                        value={weatherLocation}
                        num="12"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.internalShading", "Internal Shading: [!!]")}
                            value={langLabel.internalShading}
                            num="21"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.assumed?", "Assumed? [!!]")}
                            value={langLabel.internalShadingAssumed}
                            num="21a"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.windExposure", "Wind Exposure: [!!]")}
                        value={langLabel.windExposure}
                        num="20"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.occupants", "Occupants [!!]")}
                            value={occupants}
                            num="22"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.assumed?", "Assumed? [!!]")}
                            value={langLabel.occupantsAssumed}
                            num="22a"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <Field
                        label={t("f280pdf.windSheltering", "Wind Sheltering: [!!]")}
                        value={langLabel.windSheltering}
                        num="20a"
                        containerStyle={styles.borderRight}
                    />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.ventilated", "Ventilated? [!!]")}
                            value={langLabel.ventilated}
                            num="13"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.hrv", "HRV/ERV? [!!]")}
                            value={langLabel.hrv}
                            num="14"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <View
                        style={[styles.fieldCell, styles.borderRight, { flexDirection: "row", alignItems: "center" }]}
                    >
                        <Text style={[styles.smallLabel, { width: 90, textAlign: "center" }]}>
                            {t("f280pdf.units", "Units: [!!]")}
                        </Text>
                        <View style={styles.selectionBoxContainer}>
                            <Checkbox checked={units === "Imperial"} label={t("f280pdf.imperial", "Imperial [!!]")} />
                            <Checkbox checked={units === "Metric"} label={t("f280pdf.metric", "Metric [!!]")} />
                        </View>
                        <NumField>23</NumField>
                    </View>
                    <SplitCell>
                        <Field
                            label={t("f280pdf.ase", "ASE %: [!!]")}
                            value={langLabel.recoveryEff}
                            num="15"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.atre", "ATRE %: [!!]")}
                            value={hrvAtre}
                            num="15a"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>

                {/* Design conditions */}
                <Row style={styles.titleRow}>
                    <SmallTitle flat style={[styles.fullFlex, styles.borderRight]}>
                        {t("f280pdf.heatingDesignConds", "HEATING DESIGN CONDITIONS [!!]")}
                    </SmallTitle>
                    <SmallTitle flat style={styles.fullFlex}>
                        {t("f280pdf.coolingDesignConds", "COOLING DESIGN CONDITIONS [!!]")}
                    </SmallTitle>
                </Row>
                <Row>
                    <SplitCell style={styles.borderRight}>
                        <Field
                            label={t("f280pdf.outdoorTemp", "Outdoor Temp: [!!]")}
                            value={heating?.outdoorTemp || ""}
                            num="24"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.indoorTemp", "Indoor Temp: [!!]")}
                            value={heating?.indoorTemp || ""}
                            num="25"
                        />
                    </SplitCell>
                    <SplitCell>
                        <Field
                            label={t("f280pdf.outdoorTemp", "Outdoor Temp: [!!]")}
                            value={cooling?.outdoorTemp || ""}
                            num="27"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.range", "Range: [!!]")}
                            value={cooling?.meanTempRange || ""}
                            num="29"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>
                <Row>
                    <SplitCell style={styles.borderRight}>
                        <Field
                            label={t("f280pdf.mealSoilTemp", "Mean Soil Temp: [!!]")}
                            value={heating?.meanSoilTemp || ""}
                            num="26"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.soilCond", "Soil Conductivity: [!!]")}
                            value={langLabel.soilConductivity}
                            num="26a"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                    <SplitCell>
                        <Field
                            label={t("f280pdf.indoorTemp", "Indoor Temp: [!!]")}
                            value={cooling?.indoorTemp || ""}
                            num="28"
                            containerStyle={styles.borderRight}
                        />
                        <Field
                            label={t("f280pdf.latitude", "Latitude: [!!]")}
                            value={cooling?.latitude || ""}
                            num="30"
                            labelStyle={styles.smallLabel}
                        />
                    </SplitCell>
                </Row>

                {/* Above / below grade walls */}
                <Row style={styles.titleRow}>
                    <SmallTitle flat style={[styles.fullFlex, styles.borderRight]}>
                        {t("f280pdf.AGWalls", "ABOVE GRADE WALLS [!!]")}
                    </SmallTitle>
                    <SmallTitle flat style={styles.fullFlex}>
                        {t("f280pdf.BGWalls", "BELOW GRADE WALLS [!!]")}
                    </SmallTitle>
                </Row>
                <Row>
                    <StyleField letter="A" value={aboveGradeWalls[0]} num="31" borderRight t={t} />
                    <StyleField letter="A" value={belowGradeWalls[0]} num="34" t={t} />
                </Row>
                <Row>
                    <StyleField letter="B" value={aboveGradeWalls[1]} num="32" borderRight t={t} />
                    <StyleField letter="B" value={belowGradeWalls[1]} num="35" t={t} />
                </Row>
                <Row>
                    <StyleField letter="C" value={aboveGradeWalls[2]} num="33" borderRight t={t} />
                    <StyleField letter="C" value={belowGradeWalls[2]} num="36" t={t} />
                </Row>

                {/* Ceilings / floors on soil */}
                <Row style={styles.titleRow}>
                    <SmallTitle flat style={[styles.fullFlex, styles.borderRight]}>
                        {t("f280pdf.ceilings", "CEILINGS [!!]")}
                    </SmallTitle>
                    <SmallTitle flat style={styles.fullFlex}>
                        {t("f280pdf.floorsOnSoil", "FLOORS ON SOIL [!!]")}
                    </SmallTitle>
                </Row>
                <Row>
                    <StyleField letter="A" value={ceilings[0]} num="40" borderRight t={t} />
                    <StyleField letter="A" value={floorsOnSoil[0]} num="37" t={t} />
                </Row>
                <Row>
                    <StyleField letter="B" value={ceilings[1]} num="41" borderRight t={t} />
                    <StyleField letter="B" value={floorsOnSoil[1]} num="38" t={t} />
                </Row>
                <Row>
                    <StyleField letter="C" value={ceilings[2]} num="42" borderRight t={t} />
                    <StyleField letter="C" value={floorsOnSoil[2]} num="39" t={t} />
                </Row>

                {/* Windows / exposed floors */}
                <Row style={styles.titleRow}>
                    <SmallTitle flat style={[styles.fullFlex, styles.borderRight]}>
                        {t("f280pdf.windows", "WINDOWS [!!]")}
                    </SmallTitle>
                    <SmallTitle flat style={styles.fullFlex}>
                        {t("f280pdf.exposedFloors", "EXPOSED FLOORS [!!]")}
                    </SmallTitle>
                </Row>
                <Row>
                    <StyleField letter="A" value={windows[0]} num="49" borderRight t={t} />
                    <StyleField letter="A" value={exposedFloors[0]} num="43" t={t} />
                </Row>
                <Row>
                    <StyleField letter="B" value={windows[1]} num="50" borderRight t={t} />
                    <StyleField letter="B" value={exposedFloors[1]} num="44" t={t} />
                </Row>
                <Row>
                    <StyleField letter="C" value={windows[2]} num="51" borderRight t={t} />
                    <StyleField letter="C" value={exposedFloors[2]} num="45" t={t} />
                </Row>

                {/* Skylights / doors */}
                <Row style={styles.titleRow}>
                    <SmallTitle flat style={[styles.fullFlex, styles.borderRight]}>
                        {t("f280pdf.skylights", "SKYLIGHTS [!!]")}
                    </SmallTitle>
                    <SmallTitle flat style={styles.fullFlex}>
                        {t("f280pdf.doors", "DOORS [!!]")}
                    </SmallTitle>
                </Row>
                <Row>
                    <StyleField letter="A" value={skylights[0]} num="52" borderRight t={t} />
                    <StyleField letter="A" value={doors[0]} num="46" t={t} />
                </Row>
                <Row>
                    <StyleField letter="B" value={skylights[1]} num="53" borderRight t={t} />
                    <StyleField letter="B" value={doors[1]} num="47" t={t} />
                </Row>
                <Row>
                    <StyleField letter="C" value={skylights[2]} num="54" borderRight t={t} />
                    <StyleField letter="C" value={doors[2]} num="48" t={t} />
                </Row>

                {/* Issued + page number */}
                <Row style={styles.fillRow}>
                    <View style={[styles.fullFlex, styles.borderRight]} />
                    <SplitCell>
                        <Field
                            label={t("f280pdf.issued", "Issued: [!!]")}
                            value=""
                            num="76"
                            labelStyle={{ width: 40 }}
                            containerStyle={styles.borderRight}
                        />
                        <View style={[styles.fieldCell, { alignItems: "center" }]}>
                            <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 7 }}>
                                {t("f280pdf.page", `Page: 2 of 2 [!!]`, { current: 2, total: totalPages })}
                            </Text>
                        </View>
                    </SplitCell>
                </Row>

                <Footer
                    footnote={t("f280pdf.footnote", `Version ${APP_VERSION}. Contact: info@voltaresearch.org [!!]`, {
                        version: APP_VERSION,
                    })}
                    num="63"
                />
            </View>
        </Page>
    );
};

export default FormPage;
